import { useEffect, useRef, useState } from "react";
import moment from "moment";
import MUIDataTable from "mui-datatables";
import FilterAltOutlinedIcon from "@mui/icons-material/FilterAltOutlined";
import VisibilityIcon from "@mui/icons-material/Visibility";
import {
    Box,
    Button,
    FormControl,
    IconButton,
    InputLabel,
    MenuItem,
    Rating,
    Select,
    ThemeProvider,
    Typography,
} from "@mui/material";
import { SelectChangeEvent } from "@mui/material/Select";
import { useLocation, useNavigate } from "react-router-dom";
import ContainedButton from "components/button/contained-button";
import CustomModal from "components/customModal";
import Loader from "components/loader";
import "components/tables/style.css";
import { commonTableStyles as tableStyles, TABLE_TOKENS } from "components/tables/styles";
import { getTableTheme } from "components/tables/tableTheme";
import ROUTE_NAMES from "routes/routeNames";
import {
    SERVICE_CATEGORY,
    SERVICE_CATEGORY_LABEL,
} from "utils/serviceCategory";
import { getFeedbackList } from "./action";
import FeedbackDetailsModal, { LinkedName } from "./FeedbackDetailsModal";
import { styles } from "./styles";
import { FeedbackFilters, FeedbackOrder, FeedbackPerson, FeedbackRow } from "./types";

const EMPTY_FILTERS: FeedbackFilters = { type: "", category: "" };

const FEEDBACK_TYPE_OPTIONS = [
    { id: "", label: "All" },
    { id: "feedback", label: "Feedback" },
    { id: "complaint", label: "Complaint" },
    { id: "unresolved", label: "Unresolved" },
];

const SERVICE_TYPE_OPTIONS = [
    { id: "", label: "All" },
    ...[
        SERVICE_CATEGORY.CAR,
        SERVICE_CATEGORY.BIKE,
        SERVICE_CATEGORY.SOFA,
        SERVICE_CATEGORY.TANK,
        SERVICE_CATEGORY.CARPET,
    ].map((value) => ({
        id: `${value}`,
        label: SERVICE_CATEGORY_LABEL[value],
    })),
];

const FEEDBACK_TYPE_LABEL: Record<string, string> = {
    feedback: "Feedback",
    complaint: "Complaint",
    unresolved: "Unresolved",
};

const formatFeedbackType = (value: string) =>
    FEEDBACK_TYPE_LABEL[value] || value || "N/A";

const formatCreatedAt = (value: string) => {
    if (!value || !moment(value).isValid()) return "N/A";
    return moment(value).format("DD/MM/YYYY");
};

const isFeedbackType = (
    value: string
): value is FeedbackFilters["type"] =>
    value === "" ||
    value === "feedback" ||
    value === "complaint" ||
    value === "unresolved";

const isCategory = (value: string): value is FeedbackFilters["category"] =>
    value === "" ||
    value === "0" ||
    value === "1" ||
    value === "2" ||
    value === "3" ||
    value === "4";

const Feedback = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const requestRef = useRef(0);
    const [page, setPage] = useState(1);
    const [limit, setLimit] = useState(10);
    const [rows, setRows] = useState<FeedbackRow[]>([]);
    const [total, setTotal] = useState(0);
    const [loading, setLoading] = useState(true);
    const [failed, setFailed] = useState(false);
    const [retryId, setRetryId] = useState(0);
    const [filters, setFilters] = useState<FeedbackFilters>(EMPTY_FILTERS);
    const [draft, setDraft] = useState<FeedbackFilters>(EMPTY_FILTERS);
    const [filterOpen, setFilterOpen] = useState(false);
    const [selected, setSelected] = useState<FeedbackRow | null>(null);

    // Dashboard deep links pass ?type=feedback|complaint|unresolved
    useEffect(() => {
        const params = new URLSearchParams(location.search);
        if (!params.has("type")) return;
        const nextType = params.get("type") || "";
        if (!isFeedbackType(nextType)) return;
        setPage(1);
        setFilters((current) =>
            current.type === nextType && current.category === ""
                ? current
                : { type: nextType, category: "" }
        );
    }, [location.search]);

    useEffect(() => {
        const requestId = requestRef.current + 1;
        requestRef.current = requestId;
        setLoading(true);

        getFeedbackList({ page, limit, ...filters }, (result) => {
            if (requestId !== requestRef.current) return;
            setLoading(false);
            if (!result) {
                setFailed(true);
                return;
            }
            setFailed(false);
            setRows(result.rows);
            setTotal(result.total);
        });
    }, [page, limit, filters, retryId]);

    const openFilter = () => {
        setDraft(filters);
        setFilterOpen(true);
    };

    const applyFilter = () => {
        setPage(1);
        setFilters(draft);
        setFilterOpen(false);
    };

    const openUser = (person: FeedbackPerson) => {
        if (!person.id) return;
        navigate(`${ROUTE_NAMES.MAIN_ROUTES.USER_DETAIL}?userId=${person.id}`);
    };

    const openService = (service: FeedbackPerson) => {
        if (!service.id) return;
        navigate(
            `${ROUTE_NAMES.MAIN_ROUTES.SERVICE_DETAIL}?serviceId=${service.id}`
        );
    };

    const openOrder = (order: FeedbackOrder) => {
        if (!order.id) return;
        navigate(`${ROUTE_NAMES.MAIN_ROUTES.ORDER_DETAIL}?orderId=${order.id}`);
    };

    const appliedSummary = [
        filters.type ? `Feedback type: ${FEEDBACK_TYPE_LABEL[filters.type]}` : "",
        filters.category !== ""
            ? `Service type: ${SERVICE_CATEGORY_LABEL[Number(filters.category) as SERVICE_CATEGORY]
            }`
            : "",
    ]
        .filter(Boolean)
        .join(" · ");

    const columns = [
        {
            name: "feedback",
            label: "Feedback",
            options: {
                sort: false,
                filter: false,
                customBodyRender: (value: string) => (
                    <Typography title={value} sx={styles.feedbackText}>{value.length > 50 ? value.substring(0, 50) + "..." : value || "N/A"}</Typography>
                ),
            },
        },
        {
            name: "createdAt",
            label: "Created date",
            options: {
                sort: false,
                filter: false,
                customBodyRender: (value: string) => (
                    <Typography sx={styles.plainCell}>{formatCreatedAt(value)}</Typography>
                ),
            },
        },
        {
            name: "type",
            label: "Feedback type",
            options: {
                sort: false,
                filter: false,
                customBodyRender: (value: string) => (
                    <Typography sx={styles.plainCell}>{formatFeedbackType(value)}</Typography>
                ),
            },
        },
        {
            name: "user",
            label: "User name",
            options: {
                sort: false,
                filter: false,
                customBodyRender: (value: FeedbackPerson) => (
                    <LinkedName person={value} onOpen={openUser} />
                ),
            },
        },
        {
            name: "merchant",
            label: "Merchant name",
            options: {
                sort: false,
                filter: false,
                customBodyRender: (value: FeedbackPerson) => (
                    <LinkedName person={value} onOpen={openUser} />
                ),
            },
        },
        {
            name: "servicePartner",
            label: "Service partner name",
            options: {
                sort: false,
                filter: false,
                customBodyRender: (value: FeedbackPerson) => (
                    <LinkedName person={value} onOpen={openUser} />
                ),
            },
        },
        {
            name: "service",
            label: "Service",
            options: {
                sort: false,
                filter: false,
                customBodyRender: (value: FeedbackPerson) => (
                    <LinkedName person={value} onOpen={openService} />
                ),
            },
        },
        {
            name: "actions",
            label: "Actions",
            options: {
                sort: false,
                filter: false,
                customBodyRender: (_value: unknown, tableMeta: { rowIndex: number; tableData: FeedbackRow[] }) => {
                    const row = tableMeta.tableData[tableMeta.rowIndex];
                    return (
                        <IconButton
                            aria-label="View feedback"
                            size="small"
                            onClick={() => setSelected(row)}
                        >
                            <VisibilityIcon
                                sx={{ color: TABLE_TOKENS.accent, fontSize: "1.3rem" }}
                            />
                        </IconButton>
                    );
                },
            },
        },
    ];

    const options = {
        print: false,
        filter: false,
        search: false,
        download: false,
        serverSide: true,
        viewColumns: false,
        selectableRows: "none" as const,
        count: total,
        page: page - 1,
        rowsPerPage: limit,
        rowsPerPageOptions: [10, 25, 50, 100],
        onChangePage: (nextPage: number) => setPage(nextPage + 1),
        onChangeRowsPerPage: (nextLimit: number) => {
            setLimit(nextLimit);
            setPage(1);
        },
        textLabels: {
            body: {
                noMatch: loading ? "Loading feedback..." : "No feedback found",
            },
        },
        customToolbar: () => (
            <Button
                variant="outlined"
                startIcon={<FilterAltOutlinedIcon />}
                sx={styles.filterButton}
                onClick={openFilter}
            >
                Filter
            </Button>
        ),
    };

    const TableComponent: any = MUIDataTable;
    const showError = failed && rows.length === 0 && !loading;

    return (
        <Box>
            {showError ? (
                <Box sx={styles.stateCard}>
                    <Typography sx={styles.stateTitle}>
                        Feedback could not be loaded
                    </Typography>
                    <Typography sx={styles.stateText}>
                        Check the connection and try again.
                    </Typography>
                    <ContainedButton
                        title="Retry"
                        onClick={() => setRetryId((current) => current + 1)}
                    />
                </Box>
            ) : !loading || rows.length > 0 ? (
                <Box sx={tableStyles.paper}>
                    <ThemeProvider theme={getTableTheme()}>
                        <TableComponent
                            title={
                                <Box sx={styles.titleRow}>
                                    <Typography variant="h5" sx={tableStyles.tableHeadingTxt}>
                                        Feedback
                                    </Typography>
                                    {appliedSummary ? (
                                        <Typography sx={styles.appliedFilters}>
                                            {appliedSummary}
                                        </Typography>
                                    ) : null}
                                </Box>
                            }
                            data={rows}
                            columns={columns}
                            options={options}
                        />
                    </ThemeProvider>
                </Box>
            ) : null}
            {loading ? <Loader /> : null}
            <CustomModal
                title="Filter"
                visible={filterOpen}
                onClose={() => setFilterOpen(false)}
                maxWidth={420}
                footer={
                    <>
                        <Button
                            sx={styles.clearFilters}
                            onClick={() => setDraft(EMPTY_FILTERS)}
                        >
                            Clear
                        </Button>
                        <ContainedButton
                            title="Apply filter"
                            onClick={applyFilter}
                            disabled={loading}
                        />
                    </>
                }
            >
                <Box sx={styles.filterFields}>
                    <FilterSelect
                        label="Feedback type"
                        value={draft.type}
                        options={FEEDBACK_TYPE_OPTIONS}
                        onChange={(value) => {
                            if (!isFeedbackType(value)) return;
                            setDraft((current) => ({ ...current, type: value }));
                        }}
                    />
                    <FilterSelect
                        label="Service type"
                        value={draft.category}
                        options={SERVICE_TYPE_OPTIONS}
                        onChange={(value) => {
                            if (!isCategory(value)) return;
                            setDraft((current) => ({ ...current, category: value }));
                        }}
                    />
                </Box>
                <Typography sx={styles.filterHint}>
                    Both filters are optional. The list updates when you apply them.
                </Typography>
            </CustomModal>
            <FeedbackDetailsModal
                row={selected}
                onClose={() => setSelected(null)}
                onOpenUser={openUser}
                onOpenService={openService}
                onOpenOrder={openOrder}
            />
        </Box>
    );
};

function FilterSelect({
    label,
    value,
    options,
    onChange,
}: {
    label: string;
    value: string;
    options: { id: string; label: string }[];
    onChange: (value: string) => void;
}) {
    const labelId = `feedback-filter-${label.replace(/\s+/g, "-").toLowerCase()}`;

    return (
        <FormControl fullWidth size="small" sx={{ ...tableStyles.filterFieldRoot, mt: 0 }}>
            <InputLabel id={labelId}>{label}</InputLabel>
            <Select
                labelId={labelId}
                label={label}
                value={value}
                onChange={(event: SelectChangeEvent) => onChange(event.target.value)}
            >
                {options.map((option) => (
                    <MenuItem key={`${labelId}-${option.id || "all"}`} value={option.id}>
                        {option.label}
                    </MenuItem>
                ))}
            </Select>
        </FormControl>
    );
}

export default Feedback;
